import { ID_TOKEN_KEY } from '../constants';
import {
  ApplicationError,
  BusinessCheckError,
  ForbiddenError,
  NetworkError,
  ObjectNotFoundError,
  OptimisticLockError,
  SystemError,
  UnauthorizedError,
} from './ApplicationError';

function href(path: string) {
  return `${process.env.ORIGIN_SERVER_URL}${path}`;
}

function initRequest(): RequestInit {
  return {
    mode: 'cors',
    credentials: 'include',
    headers: new Headers({
      'Content-Type': 'application/json',
      Authorization: localStorage.getItem(ID_TOKEN_KEY) ?? '',
    }),
  };
}

async function handleInvalidRes(res: Response) {
  let payload;
  try {
    payload = await res.clone().json();
  } catch {
    payload = null;
  }

  switch (res.status) {
    case 400:
      return new BusinessCheckError(payload);
    case 401:
      return new UnauthorizedError(payload);
    case 403:
      return new ForbiddenError(payload);
    case 404:
      return new ObjectNotFoundError(payload);
    case 409:
      return new OptimisticLockError(payload);

    default:
      return new SystemError(payload);
  }
}

function shouldLogToPendo(init: RequestInit, res: Response) {
  if (!window.pendo) return false;
  if (!init.method || init.method.toUpperCase() !== 'POST') return false;
  return res.url.includes('admin/tenant');
}

function buildBatchProps(body: BodyInit | null | undefined): Record<string, unknown> {
  try {
    const parsed = JSON.parse(body as string) as { tenants?: Array<Record<string, unknown>> };
    const tenants = parsed.tenants ?? [];
    return {
      type: 'batch',
      tenantCount: tenants.length,
      tenantIds: tenants.map((t) => String(t.tenant ?? '')).join(','),
      tenantAliases: tenants.map((t) => String(t.alias ?? '')).join(','),
      plans: tenants.map((t) => String(t.plan ?? '')).join(','),
      versions: tenants.map((t) => String(t.version ?? '')).join(','),
      silos: tenants.map((t) => String(t.silo ?? '')).join(','),
      modelGroups: tenants.map((t) => (t.modelGroup ? '1' : '0')).join(','),
    };
  } catch {
    return { form: body };
  }
}

async function extractErrorMessage(res: Response): Promise<string | null> {
  try {
    const errJson: unknown = await res.clone().json();
    const msg =
      typeof (errJson as { message?: unknown })?.message === 'string'
        ? (errJson as { message: string }).message
        : JSON.stringify(errJson);
    return msg.slice(0, 300);
  } catch {
    return null;
  }
}

async function handleApiLogToPendo(path: string, init: RequestInit, res: Response) {
  if (!shouldLogToPendo(init, res)) return;

  const isBatch = res.url.includes('/admin/tenants/batch');
  const payload: Record<string, unknown> = {
    event: 'submitForm',
    timestamp: Date.now(),
    url: res.url,
    responseStatus: res.status,
    responseOk: res.ok,
    ...(isBatch ? buildBatchProps(init.body) : { form: init.body }),
  };

  if (!res.ok) {
    const msg = await extractErrorMessage(res);
    if (msg !== null) payload.errorMessage = msg;
  }

  window.pendo?.track('onboarding_front', payload);
}

async function safeFetch<T>(path: string, init: RequestInit) {
  try {
    const res = await fetch(path, init);
    await handleApiLogToPendo(path, init, res);
    if (res.ok) {
      return (await res.json()) as T;
    } else {
      throw await handleInvalidRes(res);
    }
  } catch (e) {
    if (!(e instanceof ApplicationError)) throw new NetworkError();
    throw e;
  }
}

function serializeToSearchParams(params: Record<string, number | string>) {
  return Object.keys(params).length === 0
    ? ''
    : `?${new URLSearchParams(
        Object.fromEntries(Object.entries(params).map(([k, v]) => [k, v.toString()])),
      ).toString()}`;
}

async function get<T>(path: string, params: Record<string, number | string> = {}) {
  return safeFetch<T>(`${href(path)}${serializeToSearchParams(params)}`, {
    ...initRequest(),
    method: 'GET',
  });
}

async function post<T>(path: string, body: object = {}) {
  return safeFetch<T>(href(path), {
    ...initRequest(),
    method: 'POST',
    body: JSON.stringify(body),
  });
}

async function put<T>(path: string, body: object = {}) {
  return safeFetch<T>(href(path), {
    ...initRequest(),
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

async function del<T>(path: string, body: object = {}) {
  return safeFetch<T>(href(path), {
    ...initRequest(),
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

export { del, get, post, put, serializeToSearchParams };
